import React, { useMemo, useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { StyleSheet, View, ScrollView, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useRouter } from 'expo-router';
import Icon from '@/components/Icon';
import ImageStyleCard from '@/components/ImageStyleCard';
import CreateYourOwnStyleCard from '@/components/CreateYourOwnStyleCard';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import { useChatInputStore } from '@/store';
import { IMAGE_STYLES } from '@/constants/config';
import { isDarkTheme, getShadowThemeStyles } from '@/constants/Colors';

const createStyles = (colors, isXS, isDark, themeStyles) => StyleSheet.create({
  container: {
    position: 'relative',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: isXS ? 12 : 24,
    justifyContent: 'space-between',
  },
  gridItem: {
    width: '31%',
    minWidth: 0,
    marginBottom: 6,
  },
  scrollView: {
    paddingHorizontal: isXS ? 16 : 24,
  },
  scrollContent: {
    paddingRight: isXS ? 16 : 24,
    paddingLeft: Platform.OS === 'web' ? 0 : isXS ? 16 : 24,
  },
});

const createHeaderNavStyles = (colors, isDark, themeStyles) => StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  button: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.solid_color_primary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? {
      boxShadow: themeStyles.boxShadow,
    } : {}),
  },
  buttonHovered: {
    backgroundColor: colors.solid_color_secondary,
  },
});

/**
 * Nav buttons for carousel, meant to be rendered in a section header (e.g. ExploreSection headerRight).
 */
export function CarouselNavButtons({ canScrollLeft, canScrollRight, onScrollLeft, onScrollRight }) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const themeStyles = useMemo(() => getShadowThemeStyles(theme), [theme]);
  const [leftHovered, setLeftHovered] = useState(false);
  const [rightHovered, setRightHovered] = useState(false);
  const styles = useMemo(() => createHeaderNavStyles(colors, isDark, themeStyles), [colors, isDark, themeStyles]);

  return (
    <View style={styles.row}>
      <Pressable
        style={[styles.button, leftHovered && styles.buttonHovered]}
        onPress={onScrollLeft}
        onHoverIn={() => setLeftHovered(true)}
        onHoverOut={() => setLeftHovered(false)}
        disabled={!canScrollLeft}
      >
        <Icon name="chevron-left" size={16} color={colors.text_color_primary} />
      </Pressable>
      <Pressable
        style={[styles.button, rightHovered && styles.buttonHovered]}
        onPress={onScrollRight}
        onHoverIn={() => setRightHovered(true)}
        onHoverOut={() => setRightHovered(false)}
        disabled={!canScrollRight}
      >
        <Icon name="chevron-right" size={16} color={colors.text_color_primary} />
      </Pressable>
    </View>
  );
}

const ImageStyleCarousel = forwardRef(function ImageStyleCarousel({ onScrollStateChange }, ref) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const themeStyles = useMemo(() => getShadowThemeStyles(theme), [theme]);
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const scrollRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  
  const initializeImageGeneration = useChatInputStore(state => state.initializeImageGeneration);
  const requireAuth = useRequireAuth();

  const styles = useMemo(() => createStyles(colors, isXS, isDark, themeStyles), [colors, isXS, isDark, themeStyles]);

  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = scrollPosition < contentWidth - containerWidth - 10;
  const showNavButtons = ['web', 'macos'].includes(Platform.OS) && !isXS && contentWidth > containerWidth;

  useImperativeHandle(ref, () => ({
    scrollBy(amount) {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          x: scrollPosition + amount,
          animated: true,
        });
      }
    },
  }), [scrollPosition]);

  const handleScroll = useCallback((event) => {
    setScrollPosition(event.nativeEvent.contentOffset.x);
  }, []);

  const handleContentSizeChange = useCallback((width) => {
    setContentWidth(width);
  }, []);

  const handleLayout = useCallback((event) => {
    setContainerWidth(event.nativeEvent.layout.width);
  }, []);

  const scrollBy = useCallback((amount) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        x: scrollPosition + amount,
        animated: true,
      });
    }
  }, [scrollPosition]);

  const handleStylePress = useCallback((styleData) => {
    if (!styleData?.promptTemplate) return;
    // Gate before pushing to `/app` with a `pendingAutoSend` — anonymous
    // visitors would otherwise land on Home with their prompt silently
    // dropped by the auto-send `useEffect` (it requires auth).
    const ran = requireAuth(() => true, {
      source: 'images_style_carousel',
      title: 'Sign in to make an image',
      subtitle: 'Image generations are billed to your account so we can apply your credits and save the result.',
      icon: 'image',
    });
    if (ran !== true) return;
    initializeImageGeneration(styleData.promptTemplate, null, true); // autoSend = true
    router.push('/app');
  }, [initializeImageGeneration, router, requireAuth]);

  const handleCreateYourOwn = useCallback(() => {
    router.push('/app');
  }, [router]);

  const lastReported = useRef({ canScrollLeft: false, canScrollRight: false, showNavButtons: false });
  const reportState = useCallback(() => {
    const next = {
      canScrollLeft: isXS ? false : canScrollLeft,
      canScrollRight: isXS ? false : canScrollRight,
      showNavButtons: isXS ? false : showNavButtons,
    };
    if (
      lastReported.current.canScrollLeft !== next.canScrollLeft ||
      lastReported.current.canScrollRight !== next.canScrollRight ||
      lastReported.current.showNavButtons !== next.showNavButtons
    ) {
      lastReported.current = next;
      onScrollStateChange?.(next);
    }
  }, [isXS, canScrollLeft, canScrollRight, showNavButtons, onScrollStateChange]);
  React.useEffect(reportState, [reportState]);

  if (isXS) {
    return (
      <View style={styles.container}>
        <View style={styles.grid}>
          {IMAGE_STYLES.map((styleData) => (
            <View key={styleData.id} style={styles.gridItem}>
              <ImageStyleCard
                style={styleData}
                onPress={handleStylePress}
                compact
              />
            </View>
          ))}
          <View style={styles.gridItem}>
            <CreateYourOwnStyleCard onPress={handleCreateYourOwn} compact />
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container} onLayout={handleLayout}>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        onScroll={handleScroll}
        onContentSizeChange={handleContentSizeChange}
        scrollEventThrottle={16}
      >
        {IMAGE_STYLES.map((styleData) => (
          <ImageStyleCard
            key={styleData.id}
            style={styleData}
            onPress={handleStylePress}
          />
        ))}
        <CreateYourOwnStyleCard onPress={handleCreateYourOwn} />
      </ScrollView>
    </View>
  );
});

export default React.memo(ImageStyleCarousel);
